var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AwsUi } from '../../../core/runtime/src';
import LegacyClassBuilder from '../../../core/utils/src/class-builders/legacy';
import { generateButtonId } from './utils';
var allRadios = [];
export var RadioButton = AwsUi.__addComponent('awsui-radio-button', {
    releaseStatus: 'deprecated',
    callbacks: {
        initialized: function () {
            this.__defaultControlId = generateButtonId();
            if (!this.controlId) {
                this.controlId = this.__defaultControlId;
            }
            this.__addToCache();
        },
        attached: function () {
            this.__addToCache();
        },
        detached: function () {
            this.__removeFromCache();
        }
    },
    events: {
        // Fired when input focus is set onto the UI control.
        focus: {
            bubbles: false,
            cancelable: false
        },
        // Fired when the radio button changes its state.
        change: {
            bubbles: true,
            cancelable: false,
            detailType: 'RadioButton.ChangeDetail'
        }
    },
    properties: {
        // Use this property to name the radio buttons yourself, whenever you are
        // not able to use the `awsui-radio-group` component.
        // The radio group can name buttons automatically.
        name: {
            type: 'string'
        },
        // Holds the radio button's value. This is the value the radio
        // group will get when the radio button is selected.
        value: {
            type: 'string',
            announceValue: true
        },
        // Specifies that the element should be disabled, preventing the user from
        // modifying its state.
        //
        // **AngularJS:** Use `data-disabled` instead.
        disabled: {
            type: 'boolean'
        },
        // The id of the internal input. Useful for relating a label element's
        // "for" attribute to this control.
        controlId: {
            type: 'string'
        },
        // Whether the component is currently checked.
        checked: {
            type: 'boolean',
            onChange: function () {
                var _this = this;
                if (this.__managedByRadioGroup) {
                    return;
                }
                if (this.checked) {
                    allRadios
                        .filter(function (radio) { return radio.name === _this.name; })
                        .filter(function (radio) { return radio !== _this; })
                        .filter(function (radio) { return radio.checked; })
                        .forEach(function (radio) { return (radio.checked = false); });
                }
            }
        },
        // Adds aria-labelledby on the native input. Use this only with form fields that
        // contain multiple controls under the same label.
        // 1. Define a custom id inside the label
        // 2. Refer to that label from every single control under that label using this property
        ariaLabelledby: {
            type: 'string'
        },
        // Adds aria-describedby on the native input. Use this only with form fields that
        // contain multiple controls under the same label.
        // 1. Define custom ids inside the description, hint and error text
        // 2. Refer to these from every single control under that label using this property
        // 3. Refer to any other hint/description text that you provide
        ariaDescribedby: {
            type: 'string'
        },
        // Adds an `aria-label` to the native input. Only use this when you do not have a
        // visible label for this control.
        ariaLabel: {
            type: 'string'
        },
        __groupLabelId: {
            type: 'string'
        },
        // Set by the radio group to indicate that the state of the button will be
        // managed externally.
        __managedByRadioGroup: {
            type: 'boolean',
            onChange: function () {
                if (this.__managedByRadioGroup) {
                    this.__removeFromCache();
                }
            },
            announceValue: true
        },
        // Set by the tiles component. The tiles component needs to render a label around the radio content itself to ensure correct click interaction.
        // Duplicate labels don't work well with screen readers, so we don't render a label in this component when this flag is set.
        __isTile: {
            type: 'boolean',
            defaultValue: false
        }
    },
    functions: {
        // Sets input focus onto the UI control.
        focus: function () {
            this.node.querySelector('input').focus();
        },
        __getRadioCache: function () {
            return allRadios;
        },
        __inCache: function () {
            return allRadios.indexOf(this) > -1;
        },
        __addToCache: function () {
            if (this.__managedByRadioGroup) {
                return;
            }
            if (!this.__inCache()) {
                allRadios.push(this);
            }
        },
        __removeFromCache: function () {
            if (this.__inCache()) {
                allRadios.splice(allRadios.indexOf(this), 1);
            }
        }
    },
    regions: {
        // Label for the input.
        label: {
            isDefault: true
        },
        // Extra description shown below the label.
        description: {}
    },
    render: function (m, h) {
        var _this = this;
        var controlId = this.controlId || this.__defaultControlId;
        var options = h.copyDefined({
            'override-focus': true,
            id: controlId,
            disabled: this.disabled,
            checked: this.checked,
            value: this.value,
            name: this.name,
            'aria-label': this.ariaLabel,
            onfocus: function (ev) { return _this.__fireEvent('focus'); },
            onchange: function (ev) {
                // Native radio only fires when it gets checked
                _this.checked = true;
                _this.__fireEvent('change', { value: _this.value });
            }
        });
        if (this.ariaDescribedby) {
            options['aria-describedby'] = this.ariaDescribedby;
        }
        if (this.ariaLabelledby || this.__groupLabelId) {
            var labels = [this.__groupLabelId, this.ariaLabelledby, controlId + "-label"];
            options['aria-labelledby'] = labels.filter(function (a) { return !!a; }).join(' ');
        }
        var classes = new LegacyClassBuilder(this)
            .addClass('awsui-radio-button')
            .addClass('awsui-radio-button-disabled', this.disabled);
        var circleAttrs = {
            'stroke-width': 8,
            cx: 50,
            cy: 50,
            r: 46
        };
        var checkedCircleAttrs = {
            'stroke-width': 30,
            cx: 50,
            cy: 50,
            r: 35
        };
        // Tiles component renders its own label tag
        var WrapperElement = this.__isTile ? 'div' : 'label';
        var wrapperAttributes = {
            className: 'awsui-radio-button-label'
        };
        if (!this.__isTile) {
            wrapperAttributes.for = controlId;
            wrapperAttributes.id = controlId + "-label";
        }
        return (AwsUi.__createElement("div", { className: classes.toClassName() },
            AwsUi.__createElement(WrapperElement, __assign({}, wrapperAttributes),
                AwsUi.__createElement("input", __assign({ type: "radio", className: "awsui-radio-native-input" }, options)),
                AwsUi.__createElement("div", { className: "awsui-radio-button-styled-button" },
                    AwsUi.__createElement("svg", { viewBox: "0 0 100 100", "aria-hidden": "true", focusable: "false" },
                        AwsUi.__createElement("circle", __assign({ className: "awsui-radio-button-styled-circle" }, circleAttrs)),
                        AwsUi.__createElement("circle", __assign({ className: "awsui-radio-button-styled-circle-checked" }, checkedCircleAttrs)))),
                AwsUi.__createElement("div", { className: "awsui-radio-button-content" },
                    h.region('label', 'div', { className: 'awsui-radio-button-label-text' }),
                    h.region('description', 'div', { className: 'awsui-radio-button-description' })))));
    }
});
//# sourceMappingURL=radio-button.js.map